<script>
	const Vue = require('vue');
	const optimizedResize = require('utilities/optimizedResize');
	const model = require('model/model');
	const scrollManager = require('ui/core/scrollManager');

	module.exports = {
		props: {
			modelKey: {
				default: null,
				required: true
			},
			defaultTab: {
				default: undefined
			},
			variant: {
				default: null
			},
			tabs: {
				default: null
			}
		},
		data() {
			return {
				overflow: false,
				scrollX: 0,
				rightArrowPosition: 0,
				leftArrowPosition: 0,
				observer: null,
				selectedTab: undefined,
				scrollPositions: {},
				removeHashChangeHandler: null
			};
		},
		mounted() {
			this.observer = new MutationObserver(this.onMutationObserved);
			optimizedResize.add(this.needToShowArrows);

			this.removeHashChangeHandler = model.change(this.modelKey, this.onModelChange);

			this.setTextAttr();
			this.setFirstTab();
			this.setInactiveTabAttrs();
			this.needToShowArrows();

			// observe when tabs change
			this.observer.observe(this.$refs.tabList, {
				childList: true
			});
		},
		beforeDestroy() {
			optimizedResize.remove(this.needToShowArrows);
			this.removeHashChangeHandler();
			this.removeHashChangeHandler = null;

			// stop observing
			if (this.observer) {
				this.observer.disconnect();
			}
		},
		computed: {
			showLeftArrow() {
				return this.overflow && this.scrollX !== 0;
			},
			showRightArrow() {
				const { scrollWidth, offsetWidth } = this.$el || {};

				return this.overflow && this.scrollX !== scrollWidth - offsetWidth;
			}
		},
		methods: {
			needToShowArrows() {
				const el = this.$el;
				if (!el) return;

				this.overflow = false;

				if (el.offsetWidth < el.scrollWidth) {
					this.overflow = true;
				}
				this.onContainerScroll();
			},
			setFirstTab() {
				let defaultTab = this.defaultTab;

				if (!defaultTab) {
					const firstTab = this.$el.querySelector('[data-tab]:not([data-tab] [data-tab])');

					if (firstTab) {
						defaultTab = firstTab.dataset.tab;
					}
				}

				if (typeof defaultTab !== 'undefined' && !model.get(this.modelKey)) {
					model.set(this.modelKey, defaultTab);
				}
			},
			setInactiveTabAttrs() {
				Array.from(this.$el.querySelectorAll('[data-tab]')).forEach(tab => {
					if (tab.dataset.tab !== model.get(this.modelKey)) {
						tab.classList.remove('active');
						tab.tabIndex = -1;
						tab.setAttribute('aria-selected', 'false');
					}

					tab.setAttribute('role', 'tab');
					const baseId = `${this.modelKey}-${tab.dataset.tab}`;
					tab.id = `${baseId}Tab`;
					tab.setAttribute('aria-controls', `${baseId}Panel`);
				});

				Vue.nextTick(() => this.setPanelAttrs());
			},
			setActiveTabAttrs() {
				const prevTab = this.$el.querySelector('[data-tab].active');
				const activeTab = this.$el.querySelector(`[data-tab="${this.selectedTab}"]`);

				if (prevTab === activeTab) return;

				this.setInactiveTabAttrs();

				if (activeTab) {
					activeTab.classList.add('active');
					activeTab.tabIndex = 0;
					activeTab.setAttribute('aria-selected', 'true');
				}
			},
			onMutationObserved() {
				this.setActiveTabAttrs();
				this.needToShowArrows();
			},
			onContainerScroll() {
				const { scrollLeft } = this.$el;

				this.scrollX = scrollLeft;
				this.leftArrowPosition = scrollLeft;
				this.rightArrowPosition = -1 * scrollLeft;
			},
			onLeftArrowClick() {
				const el = this.$el;
				const targetPosition = Math.max(0, this.scrollX - el.offsetWidth);

				el.scrollLeft = targetPosition;
				this.scrollX = targetPosition;
			},
			onRightArrowClick() {
				const el = this.$el;
				const targetPosition = Math.min(el.scrollWidth - el.offsetWidth, this.scrollX + el.offsetWidth);

				el.scrollLeft = targetPosition;
				this.scrollX = targetPosition;
			},
			onTabClick(event) {
				const target = event.target.dataset.tab ? event.target : event.target.closest('[data-tab]');
				const targetTab = target && target.dataset.tab;

				if (targetTab) {
					event.preventDefault();
					event.stopImmediatePropagation();
					model.set(this.modelKey, targetTab);
				}
			},
			onTabKeydown(event) {
				const current = event.target.dataset.tab ? event.target : event.target.closest('[data-tab]');
				const currentTab = current && current.dataset.tab;
				const key = event.key;
				const tabElements = Array.from(this.$el.querySelectorAll('[data-tab]'));

				if (tabElements.length > 1 && currentTab && new Set(['Home', 'End', 'ArrowLeft', 'ArrowRight']).has(key)) {
					event.preventDefault();
					event.stopImmediatePropagation();

					const { tabs, currentIndex } = tabElements.reduce(
						(acc, tab, i) => {
							const tabValue = tab.dataset.tab;
							acc.tabs.push(tabValue);

							if (tabValue === currentTab) {
								acc.currentIndex = i;
							}

							return acc;
						},
						{
							tabs: [],
							currentIndex: 0
						}
					);

					const lastTabIndex = tabs.length - 1;
					let targetIndex = currentIndex;
					switch (key) {
						case 'Home':
							targetIndex = 0;
							break;
						case 'End':
							targetIndex = lastTabIndex;
							break;
						case 'ArrowLeft':
							targetIndex = currentIndex > 0 ? targetIndex - 1 : lastTabIndex;
							break;
						case 'ArrowRight':
							targetIndex = currentIndex < lastTabIndex ? targetIndex + 1 : 0;
							break;
					}

					const targetTab = tabs[targetIndex];

					model.set(this.modelKey, targetTab);
					tabElements[targetIndex].focus();
				}
			},
			onModelChange(value) {
				if (value) {
					if (this.selectedTab) {
						this.scrollPositions[this.selectedTab] = document.documentElement.scrollTop || document.body.scrollTop;
					}

					this.selectedTab = value;

					this.setActiveTabAttrs();

					scrollManager(this.scrollPositions[this.selectedTab] || 0, { priority: 2 });
				} else {
					Vue.nextTick(() => {
						this.setFirstTab();
					});
				}
			},
			setTextAttr() {
				this.$el.querySelectorAll('[data-tab]').forEach(tab => {
					tab.dataset.text = tab.textContent.trim();
				});
			},
			getPanel() {
				const panelRef =
					// Specific case when using <tab-panel> component
					(this.$parent && this.$parent.$refs && this.$parent.$refs.tabPanelBody) ||
					(this.$parent && this.$parent.$refs && this.$parent.$refs[`${this.modelKey}Panel`]);

				return (panelRef && panelRef.$el) || panelRef;
			},
			setPanelAttrs() {
				const panel = this.getPanel();
				if (panel) {
					const baseId = `${this.modelKey}-${panel.dataset.panel}`;
					panel.setAttribute('role', 'tabpanel');
					panel.id = `${baseId}Panel`;
					panel.setAttribute('aria-labelledby', `${baseId}Tab`);
				}
			}
		},
		watch: {
			tabs() {
				this.scrollPositions = {};

				if (this.selectedTab) {
					Vue.nextTick(() => {
						this.setActiveTabAttrs();
					});
				} else {
					this.setFirstTab();
				}
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'mixins/forms';
	@import 'mixins/media';
	@import 'mixins/typography';

	.tab-container {
		position: relative;
		clear: both;
		display: flex;
		align-items: center;
		overflow-x: auto;
		max-width: 100%;
		white-space: nowrap;

		.tab-list {
			button {
				display: inline-block;
				margin: 0.5rem 1rem 0 0;
				padding-bottom: 0.5rem;
				background: none;
				color: $c-slate-dark;
				border: 3px solid transparent;
				border-width: 0 0 3px 0;
				transition:
					color 0.25s,
					background-color 0.25s,
					border-color 0.25s;

				@include font-weight-buffer();

				&:last-of-type {
					margin-right: 0;
				}

				&.active,
				&:hover:not(:disabled) {
					color: $c-blue-dark;
					border-color: $c-blue-dark;
				}

				&.active {
					font-weight: 700;
				}

				&:disabled {
					cursor: not-allowed;
					pointer-events: auto;
				}

				@include media(med) {
					margin-top: 1rem;
					margin-right: 2.5rem;
				}

				@include media(lg) {
					margin-right: 1.5rem;
				}
			}

			&.pill-tabs {
				margin: 1rem 0;

				button {
					margin: 0;
					padding: 0.5rem 1rem;
					color: $c-slate-dark;
					border-bottom-width: 0;
					border-radius: 24px;

					&.active {
						color: $c-blue-dark;
						background-color: $c-babyblue;
					}

					&:hover:not(:disabled) {
						font-weight: 700;
					}

					&:hover:not(:disabled),
					&:focus {
						color: $c-blue-dark;
					}
				}
			}

			&.toggle {
				margin: 0;
				display: inline-flex;
				justify-content: center;

				button {
					@include toggle-button();

					&:focus {
						outline-offset: -3px;
					}
				}
			}

			.tab-with-hint {
				display: inline-block;

				button {
					margin-right: 0;
				}

				.tab-hint {
					border-bottom-width: 0;
				}

				@include media(med) {
					margin-right: 2.5rem;
				}

				@include media(lg) {
					margin-right: 1.5rem;
				}
			}
		}

		.tab-arrow {
			display: inline;
			position: absolute;
			padding: 0 0.5rem;
			border: none;
			font-size: 1.5rem;
			color: $c-slate-dark;
			background-color: $c-blue-lighter;
			opacity: 0.9;
			z-index: 1;
		}
	}
</style>
<template>
	<div
		class="tab-container"
		@scroll="onContainerScroll"
		@click="onTabClick"
		@keydown="onTabKeydown">
		<button
			v-if="showLeftArrow"
			@click="onLeftArrowClick"
			:style="{
				left: leftArrowPosition + 'px'
			}"
			class="tab-arrow left-tab-arrow"
			aria-label="Show previous tabs">
			<i class="fa-solid fa-angle-left"></i>
		</button>
		<div
			class="tab-list"
			:class="variant"
			role="tablist"
			ref="tabList">
			<slot></slot>
		</div>
		<button
			v-if="showRightArrow"
			@click="onRightArrowClick"
			:style="{
				right: rightArrowPosition + 'px'
			}"
			class="tab-arrow right-tab-arrow"
			aria-label="Show next tabs">
			<i class="fa-solid fa-angle-right"></i>
		</button>
	</div>
</template>
